import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { authAPI } from '../services/api';

const ResetPassword: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [token, setToken] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    const validateToken = async () => {
      const urlParams = new URLSearchParams(location.search);
      const tokenParam = urlParams.get('token');
      
      if (tokenParam) {
        setToken(tokenParam);
        try {
          const response = await authAPI.validateResetToken(tokenParam);
          
          if (response.success && response.valid) {
            setTokenValid(true);
            setUserEmail(response.email || '');
            setUserName(response.name || '');
          } else {
            setSnackbar({
              open: true,
              message: response.message || 'Enlace de recuperación inválido o expirado',
              severity: 'error',
            });
          }
        } catch (error: any) {
          setSnackbar({
            open: true,
            message: error.message || 'Error al validar el enlace',
            severity: 'error',
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: 'Token de recuperación no encontrado',
          severity: 'error',
        });
      }
      
      setLoading(false);
    };

    validateToken();
  }, [location.search]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: 'Las contraseñas no coinciden',
        severity: 'error',
      });
      return;
    }

    if (newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: 'La contraseña debe tener al menos 6 caracteres',
        severity: 'error',
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await authAPI.resetPasswordWithToken({
        token,
        nueva_contrasena: newPassword,
      });

      if (response.success) {
        setPasswordChanged(true);
        setSnackbar({
          open: true,
          message: response.message || 'Contraseña actualizada exitosamente',
          severity: 'success',
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Error al cambiar la contraseña',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!tokenValid) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 3,
            borderRadius: 3,
            maxWidth: 500,
            width: '100%',
            backgroundColor: '#f8f9fa',
            border: '2px solid #d0d7de',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#dc3545',
              mb: 1,
            }}
          >
            Enlace Inválido
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              mb: 3,
            }}
          >
            El enlace de recuperación es inválido o ha expirado.
          </Typography>
          <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              fullWidth
              sx={{
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                backgroundColor: '#007bff',
                color: '#ffffff',
                boxShadow: '0 4px 12px rgba(0, 123, 255, 0.15)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0056b3',
                  boxShadow: '0 6px 20px rgba(0, 123, 255, 0.25)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              SOLICITAR NUEVO ENLACE
            </Button>
          </Link>
        </Paper>
      </Box>
    );
  }

  if (passwordChanged) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            maxWidth: 450,
            width: '100%',
            backgroundColor: '#f8f9fa',
            border: '2px solid #d0d7de',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            textAlign: 'center',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <CheckCircle
              sx={{
                fontSize: 80,
                color: '#10b981',
                mb: 2,
              }}
            />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#333',
              mb: 2,
            }}
          >
            ¡Contraseña Actualizada!
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: '#4a5568',
              fontSize: '1.1rem',
              lineHeight: 1.6,
            }}
          >
            Tu contraseña ha sido cambiada exitosamente.
          </Typography>
          
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 4,
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          >
            Ya puedes acceder al sistema con tu nueva contraseña.
          </Typography>

          <Button
            component="a"
            href="https://contax.sigex.pe/auth/jwt/login?returnTo=https%3A%2F%2Fcontax.sigex.pe%2Fdashboard"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: '#000000',
              color: '#ffffff',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
              '&:hover': {
                backgroundColor: '#333333',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            IR AL LOGIN
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 3,
            borderRadius: 3,
            maxWidth: 500,
            width: '100%',
            backgroundColor: '#f8f9fa',
            border: '2px solid #d0d7de',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: '#333',
                mb: 1,
              }}
            >
              Nueva Contraseña
            </Typography>
            
            {userName && (
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  mb: 2,
                }}
              >
                Hola {userName}, ingresa tu nueva contraseña
              </Typography>
            )}
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Nueva Contraseña *"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label="Confirmar Contraseña *"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting}
              sx={{
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                backgroundColor: '#000000',
                color: '#ffffff',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#333333',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  backgroundColor: '#666666',
                  color: '#cccccc',
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              ) : (
                'CAMBIAR CONTRASEÑA'
              )}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ResetPassword;